export interface Player {
  id: string;
  username: string;
  firstname: string;
  lastname: string;
  mobile: string;
  email: string;
  kycStatus: 'Approved' | 'Pending' | 'Rejected';
  wallet: {
    amount: number;
  };
  status: 'Active' | 'Blocked';
  lastLogin: string;
}

export interface GameTable {
  id: string;
  tableName: string;
  betType: 'Dragon' | 'Tiger' | 'Tie' | 'Suited Tie'; // fixed type for now
  minBet: number;
  maxBet: number;
  status: 'Active' | 'Stopped' | 'Paused';
  playersOnline: number;
  roundTime: number; // in seconds (12s, 15s, etc.)
  createdAt: string;
  updatedAt: string;
}

export interface GameTableForm {
  tableName: string;
  gameSpeed: 12 | 15 | 20; // seconds
  minBetAmount: number;
  maxBetAmount: number;
  commission: number; // 0–100%, optional
  autoShuffle: boolean;
  resultMode: 'random' | 'manual';
  status: 'Active' | 'Stopped';
}

export interface GameHistory {
  roundId: string;
  userId: string;
  username: string;
  tableNumber: number;
  betType: 'dragon' | 'tiger' | 'tie' | 'Suited Tie';
  result: 'dragon' | 'tiger' | 'tie' | 'Suited Tie';
  amount: number;
  win: boolean;
  profit: number; // positive if win, negative if lose
  createdAt: string; // ISO date
}

export interface GameRoundHistory {
  roundId: number;
  dragonCard: string | null;
  tigerCard: string | null;
  result: 'Dragon' | 'Tiger' | 'Tie' | 'Suited Tie' | null;
  totalBetAmount: number;
  totalPayout: number;
  profit: number;
  loss: number;
  timestamp: string;
}

export interface AllBetEntry {
  betId: string;
  playerId: string;
  roundId: string;
  betType: 'Dragon' | 'Tiger' | 'Tie' | 'Suited Tie';
  betAmount: number;
  payout: number;
  status: 'Win' | 'Lose';
  timestamp: string;
}

export interface SuspiciousBet {
  betId: string;
  playerId: string;
  playerName: string;
  roundId: string;
  betType: 'Dragon' | 'Tiger' | 'Tie' | 'Suited Tie';
  betAmount: number;
  payout: number;
  reason: 'High Amount' | 'Repeated Pattern'; // "High Amount", "Repeated Pattern"
  timestamp: string;
}

export interface DepositEntry {
  id: string; // Unique deposit ID
  userId: string; // Player ID
  username: string; // Player name
  utrNumber: string; // UTR / Transaction number
  amount: number; // Deposit amount
  paymentMode: string; // upi / bank / card / wallet
  screenshotUrl?: string; // Image URL
  status: 'Pending' | 'Approved' | 'Rejected';
  requestedAt: string; // ISO date
}

export interface WithdrawalEntry {
  id: string; // unique id
  userId: string;
  username: string;
  accountHolder: string; // "Test Here" in screenshot
  amount: number;
  accountNumber: string; // Mobile or Account number
  ifscOrUpi: string; // IFSC / UPI ID
  status: 'Pending' | 'Approved' | 'Rejected';
  requestedAt: string; // ISO timestamp
}

export interface DepositHistory {
  id: string; // Unique deposit ID
  userId: string; // Player ID
  username: string; // Player username
  amount: number; // Deposit amount
  status: 'pending' | 'success' | 'failed';
  paymentMethod: 'upi' | 'bank' | 'wallet' | 'card' | 'manual';
  transactionId: string; // Payment gateway transaction ID
  referenceId?: string; // Optional UPI reference ID
  createdAt: string; // ISO time string
  updatedAt?: string;
}

export interface WithdrawalHistory {
  id: string; // Unique withdrawal ID
  userId: string; // Player ID
  username: string; // Player username
  amount: number; // Withdrawal amount
  status: 'pending' | 'approved' | 'rejected' | 'processing' | 'failed';
  payoutMethod: 'upi' | 'bank' | 'wallet' | 'manual';
  accountNumber?: string; // Bank account OR UPI ID
  ifscCode?: string; // Only for bank
  transactionId?: string; // Payout transaction ID (if API payout used)
  remark?: string; // Admin remark on approve/reject
  createdAt: string;
  updatedAt?: string;
}

export interface DailyRevenue {
  date: string;
  totalDeposits: number;
  totalWithdrawals: number;
  totalPayouts: number;
  netRevenue: number;
}

export interface PlayerProfitLoss {
  playerId: string;
  username: string;
  totalBet: number;
  totalWin: number;
  totalLoss: number;
  net: number;
}

export interface GameProfitLoss {
  tableId: string;
  tableName: string;
  totalBet: number;
  totalPayout: number;
  net: number;
}

export interface WithdrawalSummary {
  requestId: string;
  playerId: string;
  username: string;
  amount: number;
  transactionType: 'Credit' | 'Debit';
  direction: 'Deposit' | 'Withdrawal';
  method: string;
  status: string;
  timestamp: string;
}

export interface PlayerComplaint {
  ticketId: string;
  playerId: string;
  username: string;
  issueType: 'Payment' | 'Game Issue' | 'KYC' | 'Technical' | 'Other';
  chatHistory: string;
  status: 'Open' | 'Resolved';
  createdAt: string;
}

export interface AdminActivityLog {
  id: string;
  adminName: string;
  action: string;
  timestamp: string;
}

export interface PlayerLoginLog {
  id: string;
  playerId: string;
  username: string;
  ipAddress: string;
  timestamp: string;
}

export interface FraudDetectionLog {
  id: string;
  playerId: string;
  issue: string;
  riskLevel: 'Low' | 'Medium' | 'High';
  timestamp: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  firstName?: string;
  lastName?: string;
  role: UserRole;
  avatar?: string;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
}

export enum UserRole {
  ADMIN = 'admin',
  USER = 'user',
  MODERATOR = 'moderator',
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  name: string;
  confirmPassword: string;
}

export interface ApiResponse<T = unknown> {
  data: T;
  message: string;
  success: boolean;
  errors?: Record<string, string[]>;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface Theme {
  id: string;
  name: string;
  colors: {
    background: string;
    foreground: string;
    primary: string;
    secondary: string;
    accent: string;
    muted: string;
    border: string;
    destructive: string;
  };
}

export type ThemeMode = 'light' | 'dark' | 'system';

export interface FeatureFlags {
  betaFeatures: boolean;
  advancedAnalytics: boolean;
  experimentalUI: boolean;
}

export type Language = 'en' | 'es';

export interface TableColumn<T> {
  id: string;
  header: string;
  accessorKey?: keyof T;
  cell?: (row: T) => React.ReactNode;
  sortable?: boolean;
  filterable?: boolean;
}

export interface FormStep {
  id: string;
  title: string;
  description?: string;
  component: React.ComponentType<any>;
  validation?: any;
}
